import { useState } from 'react'
import { useRegion } from '../lib/region'
import { getAffiliateLinks } from '../data/affiliates'
import type { TaskCard } from '../lib/storage'
import { useLocale } from '../i18n/locale'
import { t } from '../i18n/strings'

// Items that pair naturally with each calculator type — surfaces gaps in the project.
const COMPANION_MAP: Record<string, string[]> = {
  tile: ['thinset mortar', 'grout', 'cement backer board', 'tile spacers'],
  paint: ['primer', "painter's tape", 'drop cloths', 'roller covers'],
  concrete: ['wire mesh', 'rebar', 'form boards'],
  mulch: ['landscape fabric', 'landscape edging', 'landscape staples'],
  flooring: ['underlayment', 'transition strips', 'flooring adhesive'],
  drywall: ['joint compound', 'drywall tape', 'primer'],
  deck: ['joist hangers', 'concrete', 'deck screws'],
  fence: ['concrete', 'gate hardware', 'post caps'],
  roofing: ['roofing felt', 'drip edge', 'roofing nails'],
  insulation: ['vapor barrier', 'insulation tape', 'utility knife'],
}

// French display labels for companion items. The English value stays the affiliate
// search query — only the visible label is localized.
const COMPANION_FR: Record<string, string> = {
  'thinset mortar': 'mortier-colle',
  grout: 'coulis',
  'cement backer board': "panneau d'appui en ciment",
  'tile spacers': 'croisillons',
  primer: 'apprêt',
  "painter's tape": 'ruban à peinture',
  'drop cloths': 'toiles de protection',
  'roller covers': 'manchons de rouleau',
  'wire mesh': 'treillis métallique',
  rebar: "barre d'armature",
  'form boards': 'planches de coffrage',
  'landscape fabric': 'membrane géotextile',
  'landscape edging': 'bordure de paysagement',
  'landscape staples': 'agrafes de paysagement',
  underlayment: 'sous-couche',
  'transition strips': 'baguettes de transition',
  'flooring adhesive': 'colle à plancher',
  'joint compound': 'composé à joints',
  'drywall tape': 'ruban à joints',
  'joist hangers': 'étriers à solive',
  concrete: 'béton',
  'deck screws': 'vis de terrasse',
  'gate hardware': 'quincaillerie de barrière',
  'post caps': 'capuchons de poteau',
  'roofing felt': 'feutre de toiture',
  'drip edge': 'larmier',
  'roofing nails': 'clous de toiture',
  'vapor barrier': 'pare-vapeur',
  'insulation tape': "ruban d'isolation",
  'utility knife': 'couteau utilitaire',
}

function missingCompanions(tasks: TaskCard[]): { for: string; item: string }[] {
  const allNames = tasks.map((t) => t.name.toLowerCase())
  const suggestions: { for: string; item: string }[] = []

  for (const t of tasks) {
    const companions = COMPANION_MAP[t.calculatorType] ?? []
    for (const c of companions) {
      const alreadyPresent = allNames.some((n) => n.includes(c.split(' ')[0]))
      if (!alreadyPresent) {
        suggestions.push({ for: t.name, item: c })
      }
    }
  }

  // Deduplicate by item name
  const seen = new Set<string>()
  return suggestions.filter(({ item }) => {
    if (seen.has(item)) return false
    seen.add(item)
    return true
  })
}

function ShopLinks({ query }: { query: string }) {
  const [region] = useRegion()
  const locale = useLocale()
  const links = getAffiliateLinks(query, region, locale)
  return (
    <span className="no-print ml-2 inline-flex gap-1">
      {links.map((l) => (
        <a
          key={l.name}
          href={l.url}
          target="_blank"
          rel="nofollow sponsored noopener"
          className="rounded border border-border px-1.5 py-0.5 text-[10px] font-medium text-text-muted hover:border-brand hover:text-brand-text"
          style={{ color: l.color }}
        >
          {l.name} ↗
        </a>
      ))}
    </span>
  )
}

// Build a CSV of every line item across the displayed tasks and trigger a download.
function downloadCsv(tasks: TaskCard[]) {
  const esc = (v: string | number | undefined) => {
    const s = String(v ?? '')
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const rows = [['Task', 'Category', 'Item', 'Qty', 'Unit', 'Est. cost']]
  for (const t of tasks) {
    if (t.lines && t.lines.length > 0) {
      t.lines.forEach((l, idx) =>
        rows.push([t.name, t.category, l.label, l.qty != null ? String(l.qty) : '', l.unit ?? '', idx === 0 && t.estimatedCost > 0 ? String(t.estimatedCost) : '']),
      )
    } else {
      rows.push([t.name, t.category, t.summary ?? '', '', '', t.estimatedCost > 0 ? String(t.estimatedCost) : ''])
    }
  }
  const csv = rows.map((r) => r.map(esc).join(',')).join('\r\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `renosheets-buy-sheet-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export default function BuySheet({ tasks }: { tasks: TaskCard[] }) {
  const locale = useLocale()
  const tr = (k: string) => t(locale, k)
  const active = tasks.filter((t) => t.column !== 'done')
  const suggestions = missingCompanions(active)
  const [showDone, setShowDone] = useState(false)
  const displayTasks = showDone ? tasks : active

  if (tasks.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border bg-surface-2 p-6 text-center text-text-subtle">
        {tr('buysheet.empty')}
      </p>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-text">{tr('board.buySheet')}</h2>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-text-muted">
            <input type="checkbox" checked={showDone} onChange={(e) => setShowDone(e.target.checked)} />
            {tr('buysheet.includeDone')}
          </label>
          <div className="no-print flex gap-2">
            <button
              type="button"
              onClick={() => downloadCsv(displayTasks)}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-text-muted hover:bg-surface-2 hover:text-text"
            >
              {tr('buysheet.csv')}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-text-muted hover:bg-surface-2 hover:text-text"
            >
              {tr('buysheet.print')}
            </button>
          </div>
        </div>
      </div>

      {displayTasks.map((task) => (
        <div key={task.id} className="rounded-lg border border-border bg-surface p-4">
          <div className="mb-2 flex items-center gap-2">
            <span className="font-semibold text-text">{task.name}</span>
            <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-text-subtle">{tr('cat.' + task.category)}</span>
            {task.estimatedCost > 0 && (
              <span className="ml-auto text-sm font-medium text-brand-text">
                ${task.estimatedCost.toLocaleString()}
              </span>
            )}
          </div>

          {task.lines && task.lines.length > 0 ? (
            <ul className="space-y-1">
              {task.lines.map((line, i) => (
                <li key={i} className="flex items-baseline gap-1 text-sm text-text-muted">
                  <span className="min-w-0 flex-1">
                    {line.label}
                    {line.qty != null && (
                      <span className="tnum ml-1 font-medium text-text">
                        {line.qty} {line.unit}
                      </span>
                    )}
                  </span>
                  <ShopLinks query={line.label} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-text-subtle">{task.summary}</p>
          )}

          {task.note && (
            <p className="mt-2 text-xs text-text-subtle italic">Note: {task.note}</p>
          )}
        </div>
      ))}

      {suggestions.length > 0 && (
        <div className="rounded-lg border border-dashed border-brand/40 bg-brand-tint/20 p-4">
          <h3 className="mb-3 text-sm font-semibold text-text">
            {tr('buysheet.alsoNeed')}
          </h3>
          <ul className="space-y-2">
            {suggestions.map((s, i) => (
              <li key={i} className="flex items-center text-sm text-text-muted">
                <span className="flex-1">
                  <span className="font-medium text-text capitalize">{locale === 'fr' ? (COMPANION_FR[s.item] ?? s.item) : s.item}</span>
                  <span className="ml-1 text-text-subtle">({tr('buysheet.goesWith')} {s.for.split('—')[0].trim()})</span>
                </span>
                <ShopLinks query={s.item} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
